import { ClickAwayListener, Tooltip as MuiTooltip } from "@mui/material";
import type { TooltipProps as MuiTooltipProps } from "@mui/material";
import { useRef, useCallback, useEffect, useMemo, memo } from "react";
import { useState, type ReactElement } from "react";
import { debounce } from "lodash-es";

const useToolTip = () => {
    const [open, setOpen] = useState(false);

    const debouncedCloseRef = useRef<ReturnType<typeof debounce> | null>(null);

    useEffect(() => {
        debouncedCloseRef.current = debounce(() => {
            setOpen(false);
        }, 100);

        // Clean up on unmount
        return () => {
            if (debouncedCloseRef.current) {
                debouncedCloseRef.current.cancel();
            }
        };
    }, []);

    const handleToggle = useCallback(() => {
        setOpen((prev) => !prev);
    }, []);

    const handleClose = useCallback(() => {
        if (debouncedCloseRef.current) {
            debouncedCloseRef.current();
        }
    }, []);

    const handleOpen = useCallback(() => {
        if (debouncedCloseRef.current) {
            debouncedCloseRef.current.cancel();
        }
        setOpen(true);
    }, []);

    return useMemo(
        () => ({
            open,
            handleToggle,
            handleClose,
            handleOpen,
        }),
        [open, handleToggle, handleClose, handleOpen],
    );
};

interface TooltipProps extends Omit<MuiTooltipProps, "children"> {
    children: ReactElement;
}

export const Tooltip = memo(function Tooltip({ children, ...props }: TooltipProps) {
    const { open, handleToggle, handleClose, handleOpen } = useToolTip();
    const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);

    useEffect(() => {
        setPortalContainer(document.getElementById("app-theme-root"));
    }, []);

    // Create combined slotProps
    const slotProps = useMemo(() => {
        const baseSlotProps = {
            popper: {
                container: portalContainer,
                onMouseEnter: handleOpen,
                onMouseLeave: handleClose,
                onClick: ({ stopPropagation }: { stopPropagation?: () => void }) => {
                    stopPropagation?.();
                },
            },
            tooltip: {
                // Enforce max width and wrap long text
                className:
                    "max-w-[400px] shadow-xl whitespace-normal break-words card-bg text-dark text-[12px] font-sans border border-primary/20 tracking-tight",
            },
            arrow: {
                className: "text-primary",
            },
        };

        // Merge with any provided slotProps
        if (props.slotProps) {
            return {
                ...baseSlotProps,
                ...props.slotProps,
                // Special handling for nested objects
                popper: { ...baseSlotProps.popper, ...(props.slotProps.popper || {}) },
                tooltip: { ...baseSlotProps.tooltip, ...(props.slotProps.tooltip || {}) },
                arrow: { ...baseSlotProps.arrow, ...(props.slotProps.arrow || {}) },
            };
        }

        return baseSlotProps;
    }, [handleOpen, handleClose, portalContainer, props.slotProps]);

    return (
        <ClickAwayListener onClickAway={handleClose}>
            <MuiTooltip
                open={open}
                placement="top"
                onClick={handleToggle}
                onMouseOver={handleOpen}
                onMouseLeave={handleClose}
                arrow={true}
                slotProps={slotProps}
                className="cursor-help"
                {...props}
            >
                {children}
            </MuiTooltip>
        </ClickAwayListener>
    );
});

Tooltip.displayName = "Tooltip";
